import { useState } from "react";

import { useLocalization } from "@fluent/react";
import { Chip, Popover } from "@narmi/design_system";

import { ReceiptSort } from "../../hooks/useReceiptsList";

import styles from "./ReceiptsSortDropdown.module.scss";

interface ReceiptsSortDropdownProps {
  value: ReceiptSort;
  onChange: (next: ReceiptSort) => void;
}

const OPTIONS: ReadonlyArray<{ value: ReceiptSort; labelKey: string }> = [
  {
    value: ReceiptSort.MOST_RECENT,
    labelKey: "receipts-sort-option-most-recent",
  },
  {
    value: ReceiptSort.OLDEST_FIRST,
    labelKey: "receipts-sort-option-oldest-first",
  },
  {
    value: ReceiptSort.CONFIDENCE,
    labelKey: "receipts-sort-option-confidence",
  },
  {
    value: ReceiptSort.HIGHEST_AMOUNT,
    labelKey: "receipts-sort-option-highest-amount",
  },
  {
    value: ReceiptSort.LOWEST_AMOUNT,
    labelKey: "receipts-sort-option-lowest-amount",
  },
];

const ReceiptsSortDropdown = ({
  value,
  onChange,
}: ReceiptsSortDropdownProps) => {
  const { l10n } = useLocalization();
  const [isOpen, setIsOpen] = useState(false);

  const activeOption =
    OPTIONS.find((option) => option.value === value) ?? OPTIONS[0];
  const chipLabel = l10n.getString("receipts-sort-chip-label", {
    current: l10n.getString(activeOption.labelKey),
  });

  const handleSelect = (next: ReceiptSort) => {
    onChange(next);
    setIsOpen(false);
  };

  const popoverContent = (
    <div
      className={styles["receipts-sort-dropdown"]}
      data-testid="receipts-sort-popover"
    >
      {OPTIONS.map((option) => {
        const isActive = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            className={`${styles["receipts-sort-option"]} button--reset`}
            data-testid={`receipts-sort-option-${option.value}`}
            onClick={() => handleSelect(option.value)}
          >
            <span>{l10n.getString(option.labelKey)}</span>
            {isActive ? (
              <span
                className="narmi-icon-check fontSize--l"
                aria-hidden="true"
              />
            ) : null}
          </button>
        );
      })}
    </div>
  );

  return (
    <div data-testid="receipts-sort-chip-wrapper">
      <Popover
        content={popoverContent}
        isOpen={isOpen}
        onUserDismiss={() => setIsOpen(false)}
      >
        <span
          className={styles["receipts-sort-chip"]}
          data-testid="receipts-sort-chip"
        >
          <Chip
            kind="secondary"
            label={chipLabel}
            startIcon="filter-3"
            onClick={() => setIsOpen((prev) => !prev)}
            endIcon={`chevron-${isOpen ? "up" : "down"}`}
          />
        </span>
      </Popover>
    </div>
  );
};

export default ReceiptsSortDropdown;
